"use client";

/**
 * Calendar Booking — inner fields (account selection + working hours + per-tool
 * instructions). Agent-type-agnostic: drives the shared calendar-config + Google
 * OAuth hooks and renders everything BELOW the master "enable" toggle. Each
 * agent domain owns its own card chrome + master toggle (the toggle wiring and
 * card styling differ per agent) and drops this in once enabled.
 *
 * Account model = voice/message: the Google Calendar credential is borrowed from
 * one of the tenant's email inboxes (explicit `inbox_id`); there is no inbox tab,
 * so a "Connect another Google account" affordance lives here.
 */

import { useState } from "react";
import { Calendar, ChevronDown, Loader2, Mail, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { WorkingHoursConfigEditor } from "./working-hours-config";
import { MeetingDefaultsConfig } from "./meeting-defaults-config";
import { OAuthScopeConnectionStatus } from "@/components/integrations/oauth/oauth-scope-connection-status";
import { ToolInstructionsDialog } from "@/components/agents/shared/dialogs/tool-instructions-dialog";
import { useCalendarIntegration } from "@/hooks/integrations/google/use-calendar-integration";
import { useGoogleIntegration } from "@/hooks/integrations/google/use-integration";
import { useTranslations } from "next-intl"


type CalendarAgentType = "email" | "voice" | "messaging";

const SCOPE_TYPE_BY_AGENT: Record<CalendarAgentType, string> = {
  email: "email_agent",
  voice: "voice_agent",
  messaging: "message_agent",
};

/** Human labels for the two calendar tools (shown in the instructions dialog title). */
const TOOL_DISPLAY_NAMES: Record<string, string> = {
  check_calendar_availability: "Check Availability",
  book_calendar_meeting: "Book Meeting",
};

interface CalendarBookingFieldsProps {
  agentId: string;
  agentType: CalendarAgentType;
  disabled?: boolean;
  /**
   * Whether the master "Calendar Booking" toggle is ON. When true but no usable
   * inbox is configured, a persistent deliverability warning is shown so the
   * "enabled but won't work" gap is explicit (not just a transient state).
   */
  enabled?: boolean;
}

export function CalendarBookingFields({
  agentId,
  agentType,
  disabled,
  enabled,
}: CalendarBookingFieldsProps) {
  const tUi = useTranslations("AgentTools.AgentsSharedCalendarCalendarBookingFields")

  const {
    config,
    inboxId,
    inboxes,
    isLoading,
    isSaving,
    error,
    loadConfig,
    updateConfig,
    connectCalendar,
  } = useCalendarIntegration({ agentId, agentType, autoLoad: true });

  const { connectGoogle, isConnecting } = useGoogleIntegration();

  // Which calendar tool's instructions dialog is open (null = none).
  const [instructionsTool, setInstructionsTool] = useState<string | null>(null);
  const scopeType = SCOPE_TYPE_BY_AGENT[agentType];

  const selectedInbox = inboxes.find((i) => i.id === inboxId) ?? null;

  if (isLoading) {
    return <p className="text-sm text-muted-foreground"><span key={tUi("loadingCalendarStatus")}>{tUi("loadingCalendarStatus")}</span></p>;
  }

  if (inboxes.length === 0) {
    return (
      <div className="space-y-3">
        <Alert className="border-amber-200 bg-amber-50">
          <AlertDescription className="text-amber-800">
            <span key={tUi("noConnectedGoogleAccountsFoundConnectOneTo")}>{tUi("noConnectedGoogleAccountsFoundConnectOneTo")}</span></AlertDescription>
        </Alert>
        <Button
          variant="outline"
          size="sm"
          onClick={() => connectGoogle({ onConnected: () => loadConfig() })}
          disabled={isConnecting || disabled}
        >
          {isConnecting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Mail className="w-4 h-4 mr-2" />
          )}
          <span><span key={tUi("connectAGoogleAccount")}>{tUi("connectAGoogleAccount")}</span></span>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Deliverability gate: tools only reach the agent when the SELECTED inbox
          carries calendar scope. Surface a persistent warning whenever booking
          is on but that's not yet true (no inbox picked / missing scope). */}
      {enabled && !selectedInbox?.hasCalendarScope && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertDescription className="text-amber-800">
            <span key={!inboxId
              ? tUi("calendarBookingIsOnButNoInboxIs")
              : tUi("calendarBookingIsOnButTheSelectedInbox")}>{!inboxId
              ? tUi("calendarBookingIsOnButNoInboxIs")
              : tUi("calendarBookingIsOnButTheSelectedInbox")}</span>
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-1.5">
        <Label htmlFor={`${agentType}-calendar-inbox`}><span key={tUi("calendarInbox")}>{tUi("calendarInbox")}</span></Label>
        <Select
          value={inboxId ?? ""}
          onValueChange={(value) => updateConfig({ inbox_id: value })}
          disabled={isSaving || disabled}
        >
          <SelectTrigger id={`${agentType}-calendar-inbox`}>
            <SelectValue placeholder={tUi("selectAnInbox")} />
          </SelectTrigger>
          <SelectContent>
            {inboxes.map((inbox) => (
              <SelectItem key={inbox.id} value={inbox.id}>
                {inbox.email}
                <span key={inbox.hasCalendarScope ? "" : tUi("noCalendarAccess")}>{inbox.hasCalendarScope ? "" : tUi("noCalendarAccess")}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          <span key={tUi("calendarToolsAreDeliveredToTheAgentOnly")}>{tUi("calendarToolsAreDeliveredToTheAgentOnly")}</span></p>
      </div>

      {/* Grant calendar access for the selected inbox when missing */}
      {selectedInbox && !selectedInbox.hasCalendarScope && (
        <OAuthScopeConnectionStatus
          isLoading={false}
          inboxes={[
            {
              id: selectedInbox.id,
              email: selectedInbox.email,
              hasScope: selectedInbox.hasCalendarScope,
            },
          ]}
          onConnect={(id) => connectCalendar(id)}
          icon={Calendar}
          loadingText="Checking calendar status..."
          connectButtonText="Grant Access"
          connectedText="Connected"
          allConnectedText="Inbox has calendar access"
        />
      )}

      {/* Connect another Google account (no inbox tab for voice/message) */}
      <Button
        variant="ghost"
        size="sm"
        className="h-7 text-xs text-muted-foreground"
        onClick={() => connectGoogle({ onConnected: () => loadConfig() })}
        disabled={isConnecting || disabled}
      >
        {isConnecting ? (
          <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
        ) : (
          <Mail className="w-3 h-3 mr-1.5" />
        )}
        <span><span key={tUi("connectAnotherGoogleAccount")}>{tUi("connectAnotherGoogleAccount")}</span></span>
      </Button>

      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}

      {/* Working hours + meeting defaults (only once a usable inbox is selected) */}
      {config && selectedInbox?.hasCalendarScope && (
        <Collapsible>
          <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg border hover:bg-muted transition-colors">
            <span className="text-sm font-medium"><span key={tUi("workingHoursMeetingSettings")}>{tUi("workingHoursMeetingSettings")}</span></span>
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 space-y-4 p-3 rounded-lg border">
              <div className="space-y-4">
                <h4 className="text-sm font-medium"><span key={tUi("workingHours")}>{tUi("workingHours")}</span></h4>
                <WorkingHoursConfigEditor
                  value={config.working_hours}
                  onChange={(working_hours) => updateConfig({ working_hours })}
                  disabled={isSaving || disabled}
                />
              </div>
              <div className="border-t pt-4 space-y-3">
                <h4 className="text-sm font-medium"><span key={tUi("meetingDefaults")}>{tUi("meetingDefaults")}</span></h4>
                <MeetingDefaultsConfig
                  value={config.meeting_defaults}
                  onChange={(meeting_defaults) => updateConfig({ meeting_defaults })}
                  disabled={isSaving || disabled}
                />
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Per-tool instruction editors (the two tools shown as one feature) */}
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">
          <span key={tUi("customizeHowTheAIHandlesCalendarRequests")}>{tUi("customizeHowTheAIHandlesCalendarRequests")}</span></p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => setInstructionsTool("check_calendar_availability")}
          >
            <Settings className="w-3 h-3 mr-1.5" />
            <span><span key={tUi("checkAvailability")}>{tUi("checkAvailability")}</span></span>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => setInstructionsTool("book_calendar_meeting")}
          >
            <Settings className="w-3 h-3 mr-1.5" />
            <span><span key={tUi("bookMeeting")}>{tUi("bookMeeting")}</span></span>
          </Button>
        </div>
      </div>

      {instructionsTool && (
        <ToolInstructionsDialog
          open
          onOpenChange={(open) => !open && setInstructionsTool(null)}
          toolName={instructionsTool}
          toolDisplayName={TOOL_DISPLAY_NAMES[instructionsTool] ?? instructionsTool}
          scopeType={scopeType}
          scopeId={agentId}
        />
      )}
    </div>
  );
}
